import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import Button from "../../components/Button";
import BalanceBadge from "../../components/BalanceBadge";
import { Printer, Plus, Copy, ArrowLeft, Trash2 } from "lucide-react";
import journalEntriesAPI from "../../api/journalEntriesAPI";
import disbursementsAPI from "../../api/disbursementsAPI";
import chartOfAccountsAPI from "../../api/chartOfAccountsAPI";
import { getAccessToken } from "../../api/axiosConfig";
import ChartOfAccountsSearch from "../../components/ChartOfAccountsSearch";
import MoneyInput from "../../components/MoneyInput";
import JournalEntryGLTab from './JournalEntryGLTab';
import JournalEntryAuditTab from './JournalEntryAuditTab';
import VendorSearch from '../../components/VendorSearch';
import PredictiveMemoInput from '../../components/PredictiveMemoInput';
import { useAuth } from '../../context/AuthContext';
import useGuardedMutation from '../../hooks/useGuardedMutation';
import { useVendorTerms } from '../../utils/terminology';

const EditJournalEntryPage = () => {
  const term = useVendorTerms();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAuthReady, isAuthenticated } = useAuth();

  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [accounts, setAccounts] = useState([]); // still used for view-only fallback display
  const [entry, setEntry] = useState(null);
  const [relatedDisbursementId, setRelatedDisbursementId] = useState(null);
  const [lineReferenceDisbursementMap, setLineReferenceDisbursementMap] = useState({});
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  // Void states
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidDate, setVoidDate] = useState(new Date().toISOString().split("T")[0]);
  const [voidLoading, setVoidLoading] = useState(false);

  // Delete draft states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // UI tabs
  const [activeTab, setActiveTab] = useState("details"); // 'details' | 'gl' | 'audit'

  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split("T")[0],
    description: "",
    memo: "",
    referenceNumber: "",
    source: "manual",
    vendor: null, // Optional vendor selection
    lines: [{ account: "", accountName: "", description: "", debit: 0, credit: 0, reference: "" }],
  });

  const isEditable = entry && entry.status === "draft";
  const isVoided = entry && (entry.status === "void" || entry.status === "voided");
  const canVoid = entry && entry.status === "posted";
  const canViewGL = entry && (entry.status === "posted" || entry.status === "void" || entry.status === "voided");

  // Compute memo context for predictive input
  const memoContext = useMemo(() => {
    const accountIds = formData.lines
      .map(l => l.account)
      .filter(Boolean);
    const uniqueAccountIds = [...new Set(accountIds)];
    
    const accountNames = formData.lines
      .map(l => l.accountName)
      .filter(Boolean)
      .slice(0, 2);
    
    return {
      entryDate: formData.entryDate,
      source: formData.source,
      vendorId: formData.vendor?._id,
      vendorName: formData.vendor?.name,
      accounts: uniqueAccountIds,
      accountNames,
      entityType: 'journal'
    };
  }, [formData.lines, formData.entryDate, formData.source, formData.vendor]);

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated) return;
    const token = getAccessToken();
    if (!token) return;

    const timer = setTimeout(() => {
      fetchInitialData();
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAuthReady, isAuthenticated]);

  // Auto-save only for drafts
  useEffect(() => {
    if (!autoSaveEnabled || loading || !entry || !isEditable) return;
    if (!formData.memo) return;

    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 3000);

    return () => clearTimeout(autoSaveTimer);
    // `loading` (declared further down, combining initialLoading/submitPending/
    // duplicatePending) can't be used here — this array is evaluated eagerly
    // during render, before that later `const` initializes, which throws a
    // TDZ ReferenceError. initialLoading is available this early and is the
    // only piece of `loading` that actually changes independently of the
    // deps already listed; the effect callback above still reads the fresh
    // combined `loading` value when it actually fires (deferred, so no TDZ there).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, autoSaveEnabled, isEditable, entry, initialLoading]);

  // If entry is draft and user is on GL tab, force back to details
  useEffect(() => {
    if (!canViewGL && activeTab === "gl") setActiveTab("details");
  }, [canViewGL, activeTab]);

  const fetchInitialData = async () => {
    setInitialLoading(true);
    setError("");
    try {
      const [accountsRes, entryRes] = await Promise.all([
        chartOfAccountsAPI.getAll(),
        journalEntriesAPI.getById(id),
      ]);

      setAccounts(accountsRes.data || []);

      if (entryRes.data) {
        const loadedEntry = entryRes.data;
        setEntry(loadedEntry);

        setFormData({
          entryDate: loadedEntry.entryDate?.split("T")[0] || new Date().toISOString().split("T")[0],
          description: loadedEntry.description || "",
          memo: loadedEntry.memo || "",
          referenceNumber: loadedEntry.referenceNumber || "",
          source: loadedEntry.source || "manual",
          vendor: loadedEntry.vendor || null, // Load vendor if present
          lines: (loadedEntry.lines || []).map((line) => {
            const accountId = line.account?._id || line.account || "";
            const accountDisplay = line.account?.accountNumber
              ? `${line.account.accountNumber} - ${line.account.accountName}`
              : line.accountName || "";

            return {
              _id: line._id,
              account: accountId,
              accountName: accountDisplay,
              description: line.description || "",
              debit: line.debit || 0,
              credit: line.credit || 0,
              reference: line.reference || "",
            };
          }),
        });

        const uniqueLineReferences = Array.from(
          new Set((loadedEntry.lines || []).map((line) => String(line.reference || "").trim()).filter(Boolean))
        );

        if (uniqueLineReferences.length > 0) {
          const referenceLookups = await Promise.all(
            uniqueLineReferences.map(async (referenceNo) => {
              try {
                const disbursementRes = await disbursementsAPI.getAll({
                  page: 1,
                  limit: 1,
                  referenceNo,
                  ...(loadedEntry.accountingClient && {
                    accountingClient: loadedEntry.accountingClient?._id || loadedEntry.accountingClient,
                  }),
                });

                const relatedDisbursement = disbursementRes?.data?.[0] || null;
                return [referenceNo, relatedDisbursement?._id || null];
              } catch {
                return [referenceNo, null];
              }
            })
          );

          setLineReferenceDisbursementMap(
            referenceLookups.reduce((acc, [referenceNo, disbursementId]) => {
              acc[referenceNo] = disbursementId;
              return acc;
            }, {})
          );
        } else {
          setLineReferenceDisbursementMap({});
        }

        if (loadedEntry.source === "disbursement" && loadedEntry.referenceNumber) {
          try {
            const disbursementRes = await disbursementsAPI.getAll({
              page: 1,
              limit: 1,
              referenceNo: loadedEntry.referenceNumber,
              ...(loadedEntry.accountingClient && {
                accountingClient: loadedEntry.accountingClient?._id || loadedEntry.accountingClient,
              }),
            });

            const relatedDisbursement = disbursementRes?.data?.[0] || null;
            setRelatedDisbursementId(relatedDisbursement?._id || null);
          } catch {
            setRelatedDisbursementId(null);
          }
        } else {
          setRelatedDisbursementId(null);
        }
      }
    } catch (err) {
      setError(err?.message || "Failed to load journal entry data");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleAutoSave = async () => {
    if (loading || !isEditable) return;

    const validationErrors = validateForm();
    // Auto-save only blocks on missing header fields, same as manual draft save.
    const headerErrors = validationErrors.filter(
      (e) => /memo is required/i.test(e) || /entry date is required/i.test(e)
    );
    if (headerErrors.length > 0) return;

    try {
      const payload = {
        ...formData,
        vendor: formData.vendor?._id || null,
        lines: formData.lines.filter((line) => !isBlankLine(line)),
        autoSaved: true,
      };
      await journalEntriesAPI.update(id, payload);
      // keep silent
    } catch (err) {
      // keep silent
      console.error("Auto-save failed:", err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const isBlankLine = (line) => {
    const hasAccount = Boolean(line.account);
    const hasAmount = (line.debit || 0) > 0 || (line.credit || 0) > 0;
    return !hasAccount && !hasAmount;
  };

  const getSubstantiveLines = () =>
    formData.lines.filter((l) => !isBlankLine(l));

  const calculateTotals = () => {
    const substantive = getSubstantiveLines();
    const totalDebit = substantive.reduce((sum, line) => sum + parseFloat(line.debit || 0), 0);
    const totalCredit = substantive.reduce((sum, line) => sum + parseFloat(line.credit || 0), 0);
    const difference = Math.abs(totalDebit - totalCredit);
    // An entry with 0 or 1 substantive lines is "not yet ready" — treat as unbalanced.
    const isBalanced = substantive.length >= 2 && difference < 0.01;
    return { totalDebit, totalCredit, difference, isBalanced, substantiveCount: substantive.length };
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.memo.trim()) errors.push("Memo is required");
    if (!formData.entryDate) errors.push("Entry date is required");

    const substantive = getSubstantiveLines();

    if (substantive.length < 2) {
      errors.push("At least 2 entry lines are required (one debit, one credit)");
    }

    substantive.forEach((line) => {
      const displayIndex = formData.lines.indexOf(line) + 1;
      if (!line.account) errors.push(`Line ${displayIndex}: Account is required`);
      if ((line.debit || 0) === 0 && (line.credit || 0) === 0)
        errors.push(`Line ${displayIndex}: Either debit or credit must be greater than 0`);
      if ((line.debit || 0) > 0 && (line.credit || 0) > 0)
        errors.push(`Line ${displayIndex}: Cannot have both debit and credit amounts`);
    });

    const { isBalanced, difference, substantiveCount } = calculateTotals();
    if (substantiveCount >= 2 && !isBalanced) {
      errors.push(`Entries are out of balance by ₱${formatCurrency(difference)}`);
    }

    return errors;
  };

  const getFiscalYearAndPeriod = (dateString) => {
    const date = new Date(dateString);
    const fiscalYear = date.getFullYear();
    const accountingPeriod = new Date(date.getFullYear(), date.getMonth(), 1);
    return { fiscalYear, accountingPeriod };
  };

  const addLine = () => {
    if (!isEditable) return;

    setFormData((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          account: "",
          accountName: "",
          description: prev.description,
          debit: 0,
          credit: 0,
          reference: prev.referenceNumber,
        },
      ],
    }));
  };

  const removeLine = (index) => {
    if (!isEditable || formData.lines.length <= 1) return;
    setFormData((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }));
  };

  const updateLine = (index, field, value) => {
    if (!isEditable) return;

    setFormData((prev) => {
      const newLines = [...prev.lines];

      // MoneyInput sends a number already for debit/credit
      if (field === "debit" || field === "credit") {
        const numValue = Number(value) || 0;

        if (field === "debit" && numValue > 0) newLines[index].credit = 0;
        if (field === "credit" && numValue > 0) newLines[index].debit = 0;

        newLines[index][field] = numValue;
      } else {
        newLines[index][field] = value;
      }

      return { ...prev, lines: newLines };
    });
  };

  const handleChartOfAccountSelect = useCallback((account, index) => {
    setFormData((prev) => {
      const newLines = [...prev.lines];

      if (!account) {
        newLines[index] = { ...newLines[index], account: "", accountName: "" };
        return { ...prev, lines: newLines };
      }

      newLines[index] = {
        ...newLines[index],
        account: account._id,
        accountName: `${account.accountNumber} - ${account.accountName}`,
      };

      return { ...prev, lines: newLines };
    });
  }, []);

  const { mutate: handleSubmit, isPending: submitPending } = useGuardedMutation(async (action) => {
    if (!isEditable) {
      setError("Cannot modify posted journal entries");
      return;
    }

    const validationErrors = validateForm();

    if (action === "saveAndPost") {
      if (validationErrors.length > 0) {
        setError(validationErrors.join(", "));
        return;
      }
    } else if (action === "saveAsDraft") {
      // For drafts, only enforce header-level fields. Per-line and balance
      // errors are intentional mid-encoding state and must not block saving.
      const headerErrors = validationErrors.filter(
        (e) => /memo is required/i.test(e) || /entry date is required/i.test(e)
      );
      if (headerErrors.length > 0) {
        setError(headerErrors.join(", "));
        return;
      }
    }

    try {
      setError("");
      setSuccess("");

      // keep payload clean (avoid sending accountName to backend)
      const payload = {
        ...formData,
        vendor: formData.vendor?._id || null, // Include vendor ID if selected
        lines: formData.lines
          .filter((line) => !isBlankLine(line))
          .map((line) => ({
            _id: line._id,
            account: line.account,
            description: line.description,
            reference: line.reference,
            debit: Number(line.debit) || 0,
            credit: Number(line.credit) || 0,
          })),
      };

      if (action === "saveAsDraft") {
        await journalEntriesAPI.update(id, payload);
        setSuccess("Journal entry updated successfully");
        setTimeout(() => navigate(`/journal-entry/${id}`), 1200);
        return;
      }

      if (action === "saveAndPost") {
        // 1) Update first
        await journalEntriesAPI.update(id, payload);

        // 2) Refetch to ensure lines have _id
        const refreshedRes = await journalEntriesAPI.getById(id);
        const refreshed = refreshedRes?.data?.data || refreshedRes?.data;

        if (!refreshed?._id) throw new Error("Refetch failed after update (missing journal entry _id)");

        const refreshedLines = (Array.isArray(refreshed.lines) ? refreshed.lines : [])
          .filter((line) => !isBlankLine(line));
        if (refreshedLines.length === 0) throw new Error("Refetch returned no lines");

        const missingJournalLine = refreshedLines.find((l) => !l?._id);
        if (missingJournalLine) throw new Error("One or more journal lines are missing _id after update");

        const { fiscalYear, accountingPeriod } = getFiscalYearAndPeriod(refreshed.entryDate);

        // 3) Build GL entries from refreshed lines
        const generalLedgerEntries = refreshedLines.map((line) => ({
          journalEntry: refreshed._id,
          journalLine: line._id,
          account: line.account?._id || line.account,
          transactionDate: refreshed.entryDate,
          fiscalYear,
          accountingPeriod,
          debit: Number(line.debit) || 0,
          credit: Number(line.credit) || 0,
          description: line.description || refreshed.description,
          referenceNumber: line.reference || refreshed.referenceNumber,
          source: refreshed.source || "manual",
        }));

        await journalEntriesAPI.postToGeneralLedger(refreshed._id, generalLedgerEntries);

        setSuccess("Journal entry updated and posted to General Ledger successfully");
        setTimeout(() => navigate("/journal-entry"), 1200);
      }
    } catch (err) {
      console.error("Error updating journal entry:", err);

      const backendMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update journal entry";

      setError(backendMsg);
      throw err; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const handleVoid = async () => {
    if (!entry || !canVoid || isVoided) return;

    if (!voidReason.trim()) {
      setError("Void reason is required.");
      return;
    }

    try {
      setVoidLoading(true);
      setError("");
      setSuccess("");

      await journalEntriesAPI.void(id, {
        reason: voidReason.trim(),
        voidDate: voidDate || undefined,
      });

      setSuccess("Journal entry voided successfully");
      setVoidModalOpen(false);
      setVoidReason("");

      await fetchInitialData();
      setTimeout(() => navigate(`/journal-entry/${id}`), 800);
    } catch (err) {
      const backendMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to void journal entry";
      setError(backendMsg);
    } finally {
      setVoidLoading(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!entry || !isEditable) return;

    try {
      setDeleteLoading(true);
      setError("");

      await journalEntriesAPI.delete(id);

      setDeleteModalOpen(false);
      navigate("/journal-entry");
    } catch (err) {
      const backendMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to delete journal entry";
      setError(backendMsg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const { mutate: handleDuplicate, isPending: duplicatePending } = useGuardedMutation(async () => {
    if (!entry) return;

    try {
      setError("");
      setSuccess("");

      const response = await journalEntriesAPI.duplicate(entry._id);
      const created = response?.data || response;
      const createdId = created?._id;

      if (!createdId) {
        throw new Error("Journal entry duplicated but no ID was returned.");
      }

      setSuccess("Journal entry duplicated as a new draft");
      navigate(`/journal-entry/${createdId}`);
    } catch (err) {
      const backendMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to duplicate journal entry";
      setError(backendMsg);
      throw err; // let useGuardedMutation know this attempt failed — keeps the idempotency key for retry
    }
  });

  const loading = initialLoading || submitPending || duplicatePending;

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSubmit("saveAsDraft");
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        handleSubmit("saveAndPost");
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isEditable]);

  const { totalDebit, totalCredit, difference, isBalanced } = calculateTotals();

  if (loading && !entry) {
    return (
      <Layout pageTitle="Edit Journal Entry">
        <div className="max-w-6xl mx-auto px-6 py-6 bg-gray-50 min-h-screen overflow-x-hidden">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-lg">Loading journal entry...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!entry && !loading) {
    return (
      <Layout pageTitle="Edit Journal Entry">
        <div className="max-w-6xl mx-auto px-6 py-6 bg-gray-50 min-h-screen overflow-x-hidden">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            Journal entry not found or you do not have permission to view it.
          </div>
          <Button onClick={() => navigate("/journal-entry")} variant="primary">
            Back to Journal Entries
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle={isEditable ? "Edit Journal Entry" : "View Journal Entry"}>
      <div className="max-w-6xl mx-auto px-6 py-6 bg-gray-50 min-h-screen overflow-x-hidden">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-medium text-ink">
            <button
              type="button"
              onClick={() => navigate("/journal-entry")}
              className="text-primary-500 hover:text-primary-600 hover:underline"
            >
              Journal Entries
            </button>{" "}
            / {isEditable ? "Edit" : "View"}
          </h1>
        </div>

        {/* Header row */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
          <div className="flex items-center gap-3">
            <span
              className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${
                entry?.status === "draft"
                  ? "bg-ink-canvas text-ink-body"
                  : entry?.status === "posted"
                  ? "bg-success-50 text-success-700"
                  : "bg-danger-50 text-danger-700"
              }`}
            >
              {entry?.status}
            </span>
            <span className="font-mono text-sm text-gray-600">{entry?.entryNumber}</span>
          </div>

          {isEditable && (
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={autoSaveEnabled}
                  onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                  className="mr-2"
                />
                Auto-save
              </label>
              <span className="text-xs text-gray-500">Ctrl+S to save, Ctrl+Enter to post</span>
            </div>
          )}
        </div>

        {/* Notifications */}
        {!isEditable && (
          <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 mb-4">
            <strong className="font-medium">View only: </strong>
            This journal entry is {entry?.status} and cannot be modified.
            {entry?.status === "posted" && " To make changes, create a reversing entry."}
            {(entry?.status === "void" || entry?.status === "voided") && " This journal entry has been voided."}
          </div>
        )}

        {isEditable && entry?.duplicatedFrom && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700 mb-4">
            Duplicated from{" "}
            {entry.duplicatedFrom.entryNumber ? (
              <button
                type="button"
                onClick={() => navigate(`/journal-entry/${entry.duplicatedFrom._id}`)}
                className="font-mono underline hover:text-primary-800"
              >
                JE #{entry.duplicatedFrom.entryNumber}
              </button>
            ) : (
              "a prior entry"
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-800 mb-4">
            <div className="flex justify-between items-start">
              <div>
                <strong className="font-medium">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
              <button onClick={() => setError("")} className="ml-4 text-danger-800 hover:text-danger-600 text-lg">
                ×
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700 mb-4">
            <div className="flex justify-between items-start">
              <div>
                <strong className="font-medium">Success: </strong>
                <span className="block sm:inline">{success}</span>
              </div>
              <button onClick={() => setSuccess("")} className="ml-4 text-success-700 hover:text-success-600 text-lg">
                ×
              </button>
            </div>
          </div>
        )}

        {/* Persistent balance warning (for Draft view) */}
        {isEditable && !isBalanced && (
          <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800 mb-4">
            Journal entry is out of balance by ₱{difference.toFixed(2)}
          </div>
        )}

        {/* Tab navigation */}
        <div className="flex gap-8 border-b mb-4 overflow-x-auto whitespace-nowrap">
          <button
            className={`pb-2 ${
              activeTab === "details" ? "border-b-2 border-primary-600 font-medium" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("details")}
            type="button"
          >
            DETAILS
          </button>

          {canViewGL && (
            <button
              className={`pb-2 ${
                activeTab === "gl" ? "border-b-2 border-primary-600 font-medium" : "text-gray-600"
              }`}
              onClick={() => setActiveTab("gl")}
              type="button"
            >
              GL POSTINGS
            </button>
          )}

          <button
            className={`pb-2 ${
              activeTab === "audit" ? "border-b-2 border-primary-600 font-medium" : "text-gray-600"
            }`}
            onClick={() => setActiveTab("audit")}
            type="button"
          >
            AUDIT TRAIL
          </button>
        </div>

        {/* Tab content container */}
        <div className="bg-white rounded-lg shadow-md">
          {activeTab === "details" && (
            <div className="p-6">
              <div className="pb-28">
              {/* Entry Header */}
              <div className="mb-6">
                <h2 className="text-xl font-medium text-ink mb-4">Entry Header</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Entry Number</label>
                    <input
                      type="text"
                      value={entry?.entryNumber || ""}
                      className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600"
                      disabled
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Entry Date *</label>
                    <input
                      type="date"
                      lang="en-US"
                      name="entryDate"
                      value={formData.entryDate}
                      onChange={(e) => isEditable && setFormData({ ...formData, entryDate: e.target.value })}
                      className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        !isEditable ? "bg-gray-100 text-gray-600" : ""
                      }`}
                      required
                      disabled={loading || !isEditable}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Reference Number</label>
                    {isEditable ? (
                      <input
                        type="text"
                        name="referenceNumber"
                        value={formData.referenceNumber}
                        onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                        className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Optional reference"
                        disabled={loading}
                      />
                    ) : formData.source === "disbursement" && relatedDisbursementId ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/disbursements/${relatedDisbursementId}`)}
                        className="w-full border rounded px-3 py-2 bg-gray-100 text-left text-primary-500 hover:underline"
                        title="Open related disbursement"
                      >
                        {formData.referenceNumber || "-"}
                      </button>
                    ) : (
                      <input
                        type="text"
                        name="referenceNumber"
                        value={formData.referenceNumber}
                        className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600"
                        placeholder="Optional reference"
                        disabled
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Source</label>
                    <select
                      name="source"
                      value={formData.source}
                      onChange={(e) => isEditable && setFormData({ ...formData, source: e.target.value })}
                      className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        !isEditable ? "bg-gray-100 text-gray-600" : ""
                      }`}
                      disabled={loading || !isEditable}
                    >
                      <option value="manual">Manual</option>
                      <option value="sales">Sales</option>
                      <option value="purchase">Purchase</option>
                      <option value="payment">Payment</option>
                      <option value="receipt">Receipt</option>
                      <option value="adjustment">Adjustment</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Memo *</label>
                  <PredictiveMemoInput
                    value={formData.memo}
                    onChange={(memo) => isEditable && setFormData({ ...formData, memo })}
                    disabled={loading || !isEditable}
                    required
                    placeholder="Enter journal entry memo"
                    accountingClientId={entry?.accountingClient?._id || entry?.accountingClient}
                    context={memoContext}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Description (optional)</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={(e) => isEditable && setFormData({ ...formData, description: e.target.value })}
                    className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      !isEditable ? "bg-gray-100 text-gray-600" : ""
                    }`}
                    rows={2}
                    placeholder="Optional additional notes or description"
                    disabled={loading || !isEditable}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">{term.one} (optional)</label>
                  <VendorSearch
                    value={formData.vendor}
                    onSelect={(vendor) => isEditable && setFormData({ ...formData, vendor })}
                    placeholder={`Search ${term.lower} by name, code, or TIN...`}
                    disabled={loading || !isEditable}
                  />
                </div>
              </div>

              {/* Entry Lines */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-medium text-ink">Entry Lines</h2>
                  {isEditable && (
                    <Button
                      type="button"
                      onClick={addLine}
                      variant="secondary"
                      size="sm"
                      disabled={loading}
                    >
                      + Add Line
                    </Button>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account *
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Debit
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Credit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reference
                        </th>
                        {isEditable && (
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {formData.lines.map((line, index) => (
                        <tr key={line._id || index} className="hover:bg-gray-50 align-top">
                          <td className="px-4 py-3 pb-8">
                            {isEditable ? (
                              <ChartOfAccountsSearch
                                value={line.accountName || ""}
                                onChange={(val) => updateLine(index, "accountName", val)}
                                onSelect={(account) => handleChartOfAccountSelect(account, index)}
                                placeholder="Search chart of accounts..."
                                showLinkedBadge={false}
                                disabled={loading}
                                className="min-w-[280px]"
                              />
                            ) : (
                              <div className="px-2 py-1 text-sm">
                                {line.accountName ||
                                  (() => {
                                    const found = accounts.find((a) => a._id === line.account);
                                    return found ? `${found.accountNumber} - ${found.accountName}` : "-";
                                  })()}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {isEditable ? (
                              <input
                                type="text"
                                value={line.description}
                                onChange={(e) => updateLine(index, "description", e.target.value)}
                                className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                placeholder="Line description"
                                disabled={loading}
                              />
                            ) : (
                              <div className="px-2 py-1 text-sm">{line.description}</div>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {isEditable ? (
                              <div className="flex justify-end">
                                <MoneyInput
                                  value={line.debit || 0}
                                  onValueChange={(num) => updateLine(index, "debit", num)}
                                  placeholder="0"
                                  disabled={loading}
                                />
                              </div>
                            ) : (
                              <div className="text-right px-2 py-1 text-sm">
                                {line.debit > 0 ? formatCurrency(line.debit) : "-"}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {isEditable ? (
                              <div className="flex justify-end">
                                <MoneyInput
                                  value={line.credit || 0}
                                  onValueChange={(num) => updateLine(index, "credit", num)}
                                  placeholder="0"
                                  disabled={loading}
                                />
                              </div>
                            ) : (
                              <div className="text-right px-2 py-1 text-sm">
                                {line.credit > 0 ? formatCurrency(line.credit) : "-"}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-3">
                            {isEditable ? (
                              <input
                                type="text"
                                value={line.reference}
                                onChange={(e) => updateLine(index, "reference", e.target.value)}
                                className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                                placeholder="Reference"
                                disabled={loading}
                              />
                            ) : line.reference && lineReferenceDisbursementMap[line.reference] ? (
                              <button
                                type="button"
                                onClick={() => navigate(`/disbursements/${lineReferenceDisbursementMap[line.reference]}`)}
                                className="px-2 py-1 text-sm text-primary-500 hover:underline"
                                title="Open related disbursement"
                              >
                                {line.reference}
                              </button>
                            ) : (
                              <div className="px-2 py-1 text-sm">{line.reference}</div>
                            )}
                          </td>

                          {isEditable && (
                            <td className="px-4 py-3 text-center">
                              <Button
                                type="button"
                                variant="dangerGhost"
                                size="sm"
                                icon={Trash2}
                                onClick={() => removeLine(index)}
                                disabled={formData.lines.length <= 1 || loading}
                              >
                                Remove
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {isEditable && (
                    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                      <Button type="button" variant="ghost" size="sm" onClick={addLine} disabled={loading}>
                        + Add Line
                      </Button>
                    </div>
                  )}
                </div>

              </div>
              </div>

              {/* Sticky balance + actions footer */}
              <div className="sticky bottom-0 z-10 -mx-6 mt-6 bg-white/95 backdrop-blur border-t border-ink-line">
                <div className="px-6 py-3 flex items-center justify-between gap-4 border-b border-ink-line/60">
                  <BalanceBadge
                    balanced={isBalanced}
                    label={isBalanced ? "Balanced" : `Out of balance by ${formatCurrency(difference)}`}
                  />
                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <div className="text-xs text-ink-body">Total debit</div>
                      <div className="text-sm font-medium text-ink tabular-nums">{formatCurrency(totalDebit)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-ink-body">Total credit</div>
                      <div className="text-sm font-medium text-ink tabular-nums">{formatCurrency(totalCredit)}</div>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                  <Button
                    variant="ghost"
                    icon={ArrowLeft}
                    onClick={() => navigate("/journal-entry")}
                    disabled={loading}
                  >
                    Back to list
                  </Button>

                  {isEditable ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Utility — quietest */}
                      <Button
                        type="button"
                        variant="ghost"
                        icon={Printer}
                        onClick={() => window.open(`/journal-entry/${entry._id}/print`, "_blank")}
                      >
                        Print
                      </Button>

                      {/* Duplicate is offered for every status — draft/posted/void all
                          copy cleanly into a new manual draft (see POST /:id/duplicate). */}
                      <Button
                        type="button"
                        variant="outline"
                        icon={Copy}
                        onClick={handleDuplicate}
                        loading={duplicatePending}
                        disabled={loading}
                      >
                        {duplicatePending ? "Duplicating..." : "Duplicate"}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/journal-entry")}
                        disabled={loading}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleSubmit("saveAsDraft")}
                        loading={submitPending}
                        disabled={loading}
                      >
                        {submitPending ? "Saving..." : "Save draft"}
                      </Button>

                      {/* The one primary */}
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => handleSubmit("saveAndPost")}
                        loading={submitPending}
                        disabled={loading || !isBalanced}
                      >
                        {submitPending ? "Posting..." : "Post entry"}
                      </Button>

                      {/* Destructive — separated, low-stakes (unposted draft) */}
                      <div className="ml-2 pl-3 border-l border-ink-line">
                        <Button
                          type="button"
                          variant="dangerOutline"
                          onClick={() => setDeleteModalOpen(true)}
                          disabled={loading}
                        >
                          Delete draft
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      {/* Utility — quietest */}
                      <Button
                        variant="ghost"
                        icon={Printer}
                        onClick={() => window.open(`/journal-entry/${entry._id}/print`, "_blank")}
                      >
                        Print
                      </Button>

                      {/* Secondary actions */}
                      <Button variant="outline" icon={Plus} onClick={() => navigate(`/journal-entry/new`)}>
                        Add new
                      </Button>

                      {/* Duplicate is the primary-intent action here, but a read-only
                          record has no true page primary — kept outline. */}
                      <Button
                        variant="outline"
                        icon={Copy}
                        onClick={handleDuplicate}
                        loading={duplicatePending}
                        disabled={loading}
                      >
                        {duplicatePending ? "Duplicating..." : "Duplicate"}
                      </Button>

                      {/* Destructive — separated, outline not filled */}
                      {canVoid && !isVoided && (
                        <div className="ml-2 pl-3 border-l border-ink-line">
                          <Button
                            variant="dangerOutline"
                            onClick={() => {
                              setVoidDate(new Date().toISOString().split("T")[0]);
                              setVoidReason("");
                              setVoidModalOpen(true);
                            }}
                            disabled={loading}
                          >
                            Void entry
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Void Modal */}
              {voidModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <div className="w-full max-w-lg rounded-lg bg-white shadow-lg">
                    <div className="border-b px-5 py-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Void Journal Entry</h3>
                      <button
                        onClick={() => setVoidModalOpen(false)}
                        className="text-gray-500 hover:text-gray-700 text-xl"
                        type="button"
                      >
                        ×
                      </button>
                    </div>

                    <div className="px-5 py-4 space-y-4">
                      <div className="rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm text-warning-800">
                        This will create reversing General Ledger entries and mark the entry as void — it stays on
                        record with a void status, it does not disappear.
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Void Date</label>
                        <input
                          type="date"
                          lang="en-US"
                          value={voidDate}
                          onChange={(e) => setVoidDate(e.target.value)}
                          className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                          disabled={voidLoading}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                        <textarea
                          value={voidReason}
                          onChange={(e) => setVoidReason(e.target.value)}
                          rows={3}
                          className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                          placeholder="e.g. Wrong account used, duplicate entry, incorrect amount..."
                          disabled={voidLoading}
                        />
                      </div>
                    </div>

                    <div className="border-t px-5 py-4 flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setVoidModalOpen(false)}
                        disabled={voidLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={handleVoid}
                        loading={voidLoading}
                        disabled={voidLoading || !voidReason.trim()}
                      >
                        {voidLoading ? "Voiding..." : "Confirm void"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Draft Modal */}
              {deleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                  <div className="w-full max-w-lg rounded-lg bg-white shadow-lg">
                    <div className="border-b px-5 py-4 flex items-center justify-between">
                      <h3 className="text-lg font-medium text-ink">Delete draft</h3>
                      <button
                        onClick={() => setDeleteModalOpen(false)}
                        className="text-gray-500 hover:text-gray-700 text-xl"
                        type="button"
                        disabled={deleteLoading}
                      >
                        ×
                      </button>
                    </div>

                    <div className="px-5 py-4">
                      <div className="rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-800">
                        This will permanently delete this draft journal entry. It has not been posted to the GL, so
                        nothing else is affected — but this action cannot be undone.
                      </div>
                    </div>

                    <div className="border-t px-5 py-4 flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDeleteModalOpen(false)}
                        disabled={deleteLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={handleDeleteDraft}
                        loading={deleteLoading}
                        disabled={deleteLoading}
                      >
                        {deleteLoading ? "Deleting..." : "Delete draft"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "gl" && (
            <div className="p-6">
              <JournalEntryGLTab
                journalEntryId={entry?._id}
                status={entry?.status}
              />
            </div>
          )}

         {activeTab === 'audit' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <JournalEntryAuditTab entryId={entry?._id} accounts={accounts} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EditJournalEntryPage;